import PrepareRecordingModal from "@/components/PrepareRecordingModal";
import SearchSmartDebrief from "@/components/Search";
import { useRecordingStatus } from "@/contexts/RecordingStatus/useRecordingStatus";
import { useCustomParams } from "@/hooks/useCustomParams";
import {
  Breakpoint,
  Button,
  ButtonSize,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  HeadingL,
  HorizontalFlex,
  PopoverSize,
  Space,
  UploadIcon,
} from "@enxcs/dap-ui-stratos";
import { FC, lazy, Suspense, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionMenuStyled, HeaderContainer } from "./styles";

const UploadRecordingModal = lazy(() => import("@/components/UploadRecordingModal"));

export type PropsTypes = {
  triggerNewRecordingModal: boolean;
  setTriggerNewRecordingModal: (value: boolean) => void;
};

const RecordingListHeader: FC<PropsTypes> = ({ triggerNewRecordingModal = false, setTriggerNewRecordingModal }) => {
  const [openUploadRecording, setOpenUploadRecording] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const actionMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const { isRecording } = useRecordingStatus();

  const { t } = useTranslation();

  const { onChangeSearchTerm } = useCustomParams();

  const actionMenuOptions = useMemo(
    () => [
      {
        icon: CalendarIcon,
        label: t("recordMenu.prepare"),
        onClick: () => {
          setTriggerNewRecordingModal(true);
        },
      },
      {
        icon: UploadIcon,
        label: t("recordMenu.upload"),
        onClick: () => {
          setOpenUploadRecording(true);
        },
        disabled: isRecording,
      },
    ],
    [isRecording, setTriggerNewRecordingModal, t],
  );

  return (
    <HeaderContainer>
      <HorizontalFlex
        data-testid="recording-list-header-container"
        $justify="space-between"
        $align="flex-start"
        $gap={Space.V8}
        $breakpoint={Breakpoint.M}
        $grow
      >
        <HeadingL>{t("recordsLibrary.title")}</HeadingL>

        <HorizontalFlex $gap={Space.V16} $justify="space-between">
          <Button
            size={ButtonSize.M}
            label={t("recordMenu.label")}
            rightIcon={!isActionMenuOpen ? ChevronDownIcon : ChevronUpIcon}
            elementRef={actionMenuTriggerRef}
            ariaExpanded={isActionMenuOpen}
            ariaControls="recording-list-action-menu"
            ariaHaspopup="menu"
            onClick={() => setIsActionMenuOpen((prev) => !prev)}
          />

          {isActionMenuOpen && (
            <ActionMenuStyled
              options={actionMenuOptions}
              size={PopoverSize.S}
              triggerRef={actionMenuTriggerRef}
              id="recording-list-action-menu"
              onClose={() => setIsActionMenuOpen(false)}
            />
          )}
        </HorizontalFlex>
      </HorizontalFlex>
      <SearchSmartDebrief onSearch={onChangeSearchTerm} />

      {triggerNewRecordingModal && <PrepareRecordingModal onClose={() => setTriggerNewRecordingModal(false)} />}
      {openUploadRecording && (
        <Suspense fallback={null}>
          <UploadRecordingModal onClose={() => setOpenUploadRecording(false)} />
        </Suspense>
      )}
    </HeaderContainer>
  );
};

export default RecordingListHeader;
